import { Ref, forwardRef, useMemo } from "react"
import { DateTime } from "luxon"
import * as yup from "yup"
import { Form, Formik, FormikProps } from "formik"
import { CreateEditMessageFormValues, MessageAdditionalDataChannelsType, MessageItem } from "../messagesTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { CreateUpdateMessageFormContent, MESSAGE_BODY_LIMIT } from "./CreateUpdateMessageFormContent"
import { MessageTagOption } from "./MessageTagAutoComplete"
import { ChannelCardConfig } from "./MessageChannelCard"
import { useDateTime } from "../../../../localization/useDateTime"

export type CreateUpdateMessageFormMode = "create" | "edit"
export type CreateUpdateMessageFormLocation = "dialog" | "page"

type CreateUpdateMessageFormProps = Readonly<{
    mode: CreateUpdateMessageFormMode
    location?: CreateUpdateMessageFormLocation
    initialValues?: CreateEditMessageFormValues | null
    channelOptions?: ChannelCardConfig[]
    defaultChannels?: MessageAdditionalDataChannelsType[]
    onSubmit: (formData: MessageItem) => void
}>
type FormikRef = Ref<FormikProps<CreateEditMessageFormValues>>

export const CreateUpdateMessageForm = forwardRef(
    ({ mode, initialValues, location = "page", channelOptions, defaultChannels = [MessageAdditionalDataChannelsType.IN_APP], onSubmit }: CreateUpdateMessageFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()

        const defaultInitialValues = useMemo(
            () => ({
                title: "",
                body: "",
                publishTime: dateTimeFactory.now(),
                expirationDate: null as DateTime | null,
                channels: defaultChannels,
                tag: null,
            }),
            [defaultChannels],
        )

        const commonSchemaFields = {
            title: yup.string().required(l10n.getString("create-or-edit-message-field-title-required")),
            body: yup
                .string()
                .required(l10n.getString("create-or-edit-message-field-body-required"))
                .test(
                    "is-too-long",
                    l10n.getString("create-or-edit-message-field-body-too-long", {
                        charsLimit: MESSAGE_BODY_LIMIT,
                    }),
                    (value) => (value?.length ?? 0) <= MESSAGE_BODY_LIMIT,
                ),
            publishTime: yup.mixed<DateTime>().nullable(),
            expirationDate: yup.mixed<DateTime>().nullable(),
        }

        const createSchema = yup.object({
            ...commonSchemaFields,
            channels: yup
                .array()
                .of(yup.mixed<MessageAdditionalDataChannelsType>())
                .min(1, l10n.getString("create-or-edit-message-field-channels-required"))
                .required(l10n.getString("create-or-edit-message-field-channels-required")),
            tag: yup.mixed<MessageTagOption>().required(l10n.getString("create-or-edit-message-field-tag-required")),
        })
        const editSchema = yup.object({
            ...commonSchemaFields,
        })

        const validationSchema = mode === "create" ? createSchema : editSchema

        function handleSubmit(values: CreateEditMessageFormValues) {
            const hasInfoDisplays = Array.isArray(values.channels) && values.channels.includes(MessageAdditionalDataChannelsType.INFO_DISPLAYS)

            const base: MessageItem = {
                title: values.title,
                body: values.body,
                publishTime: values.publishTime ? dateTimeFactory.toApiISOMinutesFormat(values.publishTime) : null,
                ...((hasInfoDisplays || mode === "edit") && values.expirationDate
                    ? { expirationDate: dateTimeFactory.toApiISOMinutesFormat(values.expirationDate) }
                    : {}),
            }

            const tagOption = values.tag?.value

            const tagPayload = tagOption
                ? {
                      key: tagOption,
                      displayName: l10n.getStringForEnum("create-or-edit-message-field-tag-mode", tagOption, tagOption),
                  }
                : null

            const modifyMessage =
                mode === "create"
                    ? {
                          ...base,
                          channels: values.channels,
                          ...(tagPayload && { tag: tagPayload }),
                      }
                    : base
            onSubmit(modifyMessage)
        }

        return (
            <>
                <Formik<CreateEditMessageFormValues>
                    enableReinitialize
                    initialValues={initialValues ?? defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                >
                    <Form>
                        <CreateUpdateMessageFormContent location={location} mode={mode} channelOptions={channelOptions} />
                    </Form>
                </Formik>
            </>
        )
    },
)
